import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';

import has from 'lodash/has';
import size from 'lodash/size';

import { CoreService } from 'src/app/api/core/core/core.service';
import { filter, map } from 'rxjs/operators';
import { Bump_screen } from 'src/app/models/bump-screen.model';
import { Vip } from '@hungryhungry/kafka-schema';

export enum OMBffSocketEventType {
  DOCKET,
  VIP,
}

interface ISocketRequest {
  bump_screen_id: string;
  access_key: string;
  data?: any;
}

export type SocketIOEvent = {
  event: OMBffSocketEventType | string;
  key: string;
  data: any;
};

@Injectable({
  providedIn: 'root',
})
export class SocketioService {
  private _socketEventBus: Subject<SocketIOEvent>;

  public isSupported: boolean = false;

  /** Collection of event names to dispose on disconnect */
  private _establishedListenersToDispose: string[] = [];

  private _socketIO: Socket;

  constructor(public core: CoreService) {
    // update on websocket ready
    this.isSupported = has(window, 'WebSocket') || has(window, 'MozWebSocket');

    this._socketEventBus = new Subject<SocketIOEvent>();
  }

  private initSocket() {
    this._socketIO = io(`${this.core.Active_server.bff.url}`, {
      path: '/order-manager/',
    });
  }

  /**
   * Establishes a connection to BFF Socket.IO and authorizes the session by requesting a "authenticate" event
   *
   * Authorized connections will be able to receive events from BFF.
   *
   * @param bumpScreen The bump screen to authorize
   * @returns Subscription
   */
  connect(bumpScreen: Bump_screen): Observable<any> {
    return new Observable(subscriber => {
      try {
        this.initSocket();
        this.SocketInstance.on('disconnect', reason => {
          console.log('SOCKET DISCONNECTED', reason);
          this.dispose();
        });

        this.SocketInstance.on('connect', () => {
          this.createSocketListener('authorized', success => {
            if (success) {
              success && this._listen();
              subscriber.next(<any>success);
            }
          });

          this.createSocketListener('unauthorized', reason => {
            this.disconnect();
            subscriber.error(reason);
          });

          this.SocketInstance.emit('authenticate', {
            bump_screen_id: bumpScreen.id,
            access_key: bumpScreen.access_key,
            account_id: bumpScreen.account_id,
            locations: bumpScreen.location_details.map(({ id }) => id),
          });
        });
      } catch (e) {
        subscriber.error(e);
      }
    });
  }

  /**
   * Sends an event message to BFF
   * @param eventName The event name
   * @param data The event message
   */
  public sendMessage(eventName: string, data: ISocketRequest) {
    this.SocketInstance.emit(eventName, data);
  }

  /**
   * Creates a Socket.IO event listener
   * @param event - The event name to listen
   * @returns Event subscription
   */
  public createSocketListener(event: string, cb: (val) => void) {
    this._establishedListenersToDispose = this._establishedListenersToDispose.concat([event]);
    return this.SocketInstance.on(event, cb);
  }

  /**
   * Creates event listeners
   */
  private _listen(): any {
    this.createSocketListener('message', message => {
      if (message[0] === 'disconnect') {
        this.disconnect();
      }
    });

    this.createSocketListener('docket', data => {
      console.log('Socket data received:  docket: ', data);
      if (size(data)) {
        this.broadcast(OMBffSocketEventType.DOCKET, data[0].order_id, data[0]);
      }
    });

    this.createSocketListener('vip', data => {
      if (size(data)) {
        this.broadcast(OMBffSocketEventType.VIP, data.email, data);
      }
    });
  }

  broadcast(event: OMBffSocketEventType, key: string, data?: any) {
    this._socketEventBus.next({ event, key, data });
  }

  on<T>(eventKey: OMBffSocketEventType, dataKey?: string): Observable<T> {
    return this._socketEventBus.asObservable().pipe(
      filter(({ event, key }) => {
        return eventKey === event && (!dataKey || dataKey === key);
      }),
      map(event => <T>event.data),
    );
  }

  /**
   * Removes all the event Socket.IO listeners
   */
  dispose() {
    while (this._establishedListenersToDispose.length > 0) {
      this.SocketInstance.removeAllListeners(this._establishedListenersToDispose.pop());
    }
  }

  /**
   * Removes all the event Socket.IO listeners and stops the connection
   */
  disconnect(): void {
    if (this.SocketInstance) {
      this.SocketInstance.disconnect();

      this.dispose();
      this.SocketInstance.removeAllListeners('connect');
      this.SocketInstance.removeAllListeners('disconnect');
    }
  }

  /**
   * List of all message subscription names
   * @returns Array string
   */
  getConnectedEvents(): string[] {
    return this._establishedListenersToDispose;
  }

  public get SocketInstance(): Socket {
    return this._socketIO;
  }
}
